import moment from 'moment';
import { types as sdkTypes } from './sdkLoader';
import { checkIfReferralCode } from './referralProgram';
import toPairs from 'lodash/toPairs';

const { LatLng, Money } = sdkTypes;

export const POSTAL_CODE_MIN_LENGTH = 4;
export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 256;

const isNonEmptyString = val => {
  return typeof val === 'string' && val.trim().length > 0;
};

/**
 * Validator functions and helpers for Final Forms
 */

// Final Form expects and undefined value for a successful validation
const VALID = undefined;

export const required = message => value => {
  if (typeof value === 'undefined' || value === null) {
    // undefined or null values are invalid
    return message;
  }
  if (typeof value === 'string') {
    // string must be nonempty when trimmed
    return isNonEmptyString(value) ? VALID : message;
  }
  return VALID;
};

export const requiredStringNoTrim = message => value => {
  return typeof value === 'string' && value.length > 0 ? VALID : message;
};

// DEPRECATED in favor of required
export const requiredBoolean = message => value => {
  return typeof value === 'boolean' ? VALID : message;
};

// DEPRECATED in favor of required
export const requiredAndNonEmptyString = message => value => {
  return isNonEmptyString(value) ? VALID : message;
};

export const requiredFieldArrayCheckbox = message => value => {
  if (!value) {
    return message;
  }

  const entries = toPairs(value);
  const hasSelectedValues = entries.filter(e => !!e[1]).length > 0;
  return hasSelectedValues ? VALID : message;
};

export const minLength = (message, minimumLength) => value => {
  const hasLength = value && typeof value.length === 'number';
  return hasLength && value.length >= minimumLength ? VALID : message;
};

export const maxLength = (message, maximumLength) => value => {
  if (!value) {
    return VALID;
  }
  const hasLength = value && typeof value.length === 'number';
  return hasLength && value.length <= maximumLength ? VALID : message;
};

export const nonEmptyArray = message => value => {
  return value && Array.isArray(value) && value.length > 0 ? VALID : message;
};

export const autocompleteSearchRequired = message => value => {
  return value && value.search ? VALID : message;
};

export const autocompletePlaceSelected = message => value => {
  const selectedPlaceIsValid =
    value &&
    value.selectedPlace &&
    value.selectedPlace.address &&
    value.selectedPlace.origin instanceof LatLng;
  return selectedPlaceIsValid ? VALID : message;
};

export const bookingDateRequired = inValidDateMessage => value => {
  const dateIsValid = value && value.date instanceof Date;
  return !dateIsValid ? inValidDateMessage : VALID;
};

export const bookingDatesRequired = (inValidStartDateMessage, inValidEndDateMessage) => value => {
  const startDateIsValid = value && value.startDate instanceof Date;
  const endDateIsValid = value && value.endDate instanceof Date;

  if (!startDateIsValid) {
    return inValidStartDateMessage;
  } else if (!endDateIsValid) {
    return inValidEndDateMessage;
  } else {
    return VALID;
  }
};

// Source: http://www.regular-expressions.info/email.html
// See the link above for an explanation of the tradeoffs.
const EMAIL_RE = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

export const emailFormatValid = message => value => {
  return value && EMAIL_RE.test(value) ? VALID : message;
};

export const moneySubUnitAmountAtLeast = (message, minValue) => value => {
  return value instanceof Money && value.amount >= minValue ? VALID : message;
};

export const moneySubUnitAmountIsLessThan = (message, minValue) => value => {
  return value instanceof Money && value.amount < minValue ? VALID : message;
};
export const moneySubUnitAmountIsInBetween = (messages, minValue, maxValue) => value => {
  return value instanceof Money && value.amount < minValue
    ? messages.minMessage
    : value.amount > maxValue
    ? messages.maxMessage
    : VALID;
};

export const moneySubUnitAmountIsGreaterThan = (message, maxValue) => value => {
  return value instanceof Money && value.amount > maxValue ? VALID : message;
};

const parseNum = str => {
  const num = Number.parseInt(str, 10);
  return Number.isNaN(num) ? null : num;
};

export const ageAtLeast = (message, minYears, day, month) => year => {
  const dayNum = parseNum(year.day);
  const monthNum = parseNum(year.month);
  const yearNum = parseNum(year.year);

  // day, month, and year needs to be numbers
  if (dayNum !== null && monthNum !== null && yearNum !== null) {
    const now = moment();
    const age = new Date(yearNum, monthNum - 1, dayNum);
    const ageInYears = now.diff(moment(age), 'years', true);

    return age && age instanceof Date && ageInYears >= minYears ? VALID : message;
  }
  return message;
};

export const ageAtLeastSignUp = (message, minYears) => value => {
  const { year, month, day } = value;
  const dayNum = parseNum(day);
  const monthNum = parseNum(month);
  const yearNum = parseNum(year);
  // day, month, and year needs to be numbers
  if (dayNum !== null && monthNum !== null && yearNum !== null) {
    const now = moment();
    const age = new Date(yearNum, monthNum - 1, dayNum);
    const ageInYears = now.diff(moment(age), 'years', true);

    return age && age instanceof Date && ageInYears >= minYears ? VALID : message;
  }
  return message;
};

const EMAIL_REGEXP = /([A-Z0-9._%+-])+@([A-Z0-9.-])+\.[A-Z]{2,}/i;

const PHONE_NO_REGEXP = /([0-9]?[+-.,]?)*([0-9][+-., ]*)([0-9][+-., ]*){6}[0-9]([0-9]?[+-.,]?)*/;

const DOMAIN_REGEXP = new RegExp(
  '^(https?:\\/\\/)?' + // protocol
  '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
  '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
  '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
  '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
    '(\\#[-a-z\\d_]*)?$',
  'i'
);

// const emailRegex = /\S+@\S+\.\S+/;
// const phoneRegex = /\(?([0-9]{3})\)?([ .-]?)([0-9]{3})\2([0-9]{4})/;
// const domainRegex = /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/;

export const checkDomainInMessage = message => {
  if (!message) {
    return false;
  }
  const splitStr = message.split(' ');
  const domainStrings = splitStr.filter(text => !!DOMAIN_REGEXP.test(text));
  return domainStrings.length ? true : false;
};

export const listDomainInMessage = message => {
  const splitStr = message.split(' ');
  const domainStrings = splitStr.filter(text => !!DOMAIN_REGEXP.test(text));
  return domainStrings;
};

const emailRegex = /^(?:[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}|(?:\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-zA-Z0-9-]*[a-zA-Z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\]))$/;

// AU regex - covers all area codes 2-8 (including parentheses)
const australiaPhoneRegex = /(?:\+61\s?|\(?0\)?)(?:[2-8]\d(?:[\s.-]?\d){7}|4\d{2}(?:[\s.-]?\d){6})(?:\s*ext\s*\d+)?/;

// SG regex - covers both international and domestic formats (including parentheses)
const singaporePhoneRegex = /(?:\+65\s?|\(?\)?)[689]\d{3}[\s.-]?\d{4}/;

// Generic digit sequence (5+ digits to avoid masking years like 2025)
// More specific to avoid legitimate content
const genericDigitsRegex = /\b(?:(?!(?:19|20)\d{2})\d{5,})\b(?![a-zA-Z%])/;

// Obfuscated phone numbers with letters/special chars mixed with digits
// Matches patterns like: 9a8b4c6d7e8v9g0, 98989abcf45677, 8ab7cv6bb5vv9vv0bb3bb2bb
// More specific to avoid legitimate text
const obfuscatedPhoneRegex = /\b(?:[a-zA-Z]*\d[a-zA-Z]*){5,}\b(?![a-zA-Z])(?![a-zA-Z\s]*\b(?:am|pm|degrees|floor|of|tomorrow|December|until|for|from|to|Price|Temperature|Available|Meeting|Discount|year|Flight|Room|Chapter|Version|Page)\b)/i;

// Phone numbers with spaces between digits (like: 9  4  5  6  7  8  9  0)
// More specific to avoid time patterns
const spacedPhoneRegex = /\b(?:\d\s*){5,}\d\b(?![a-zA-Z%])(?![a-zA-Z\s]*\b(?:am|pm|degrees|floor|of|tomorrow|December|until|for|from|to|Price|Temperature|Available|Meeting|Discount|year|Flight|Room|Chapter|Version|Page)\b)/i;

// Full-width Unicode characters (like ＋６５ ８８２５ ７８３３)
const unicodePhoneRegex = /[\uFF10-\uFF19\uFF0B\uFF0D\s]+/;

// Phone numbers with various separators and extensions
const complexPhoneRegex = /\b(?:\+?\d{1,4}[:\-.\/]\d{1,4}[:\-.\/]?\d{1,4}(?:\s*ext\s*\d+)?|\d{2,}[:\-.\/]\d{2,}[:\-.\/]?\d{2,}(?:\s*ext\s*\d+)?)\b/;

// Enhanced pattern for creative separators
const creativeSeparatorRegex = /\b(?:\+?\d{1,4}[:\-.\/_|]\d{1,4}[:\-.\/_|]?\d{1,4})\b/;

// Pattern for extensions (ext, x, #)
const extensionRegex = /\b(?:\+?\d{1,4}[:\-.\/]\d{1,4}[:\-.\/]?\d{1,4}(?:\s*(?:ext|x|#)\s*\d+)?)\b/;

// Pattern to detect 5+ consecutive number words (phone number representation)
// Matches sequences like "six eight five nine three" (5+ number words in a row)
// Includes common obfuscation variants (e.g., "won" for "one", "ate" for "eight")
const numberWordPattern = '(?:zero|o|zee|zer0|one|won|wun|on3|two|to|too|tu|tw0|three|thr33|free|tree|four|for|fore|fo|f0ur|five|fiv|fye|fyv|phive|six|sicks|sixx|syx|s1x|seven|sevn|se7en|sven|eight|ate|ait|eht|8ight|nine|nyne|nien|nein|nin|n1ne)';
const sequentialNumberWordsRegex = new RegExp(
  `\\b${numberWordPattern}(?:\\s+${numberWordPattern}){4,}\\b`,
  'gi'
);

const combinedPhoneRegex = new RegExp(
  `(?:${australiaPhoneRegex.source}|${singaporePhoneRegex.source}|${genericDigitsRegex.source}|${obfuscatedPhoneRegex.source}|${spacedPhoneRegex.source}|${unicodePhoneRegex.source}|${complexPhoneRegex.source}|${creativeSeparatorRegex.source}|${extensionRegex.source})`,
  'g'
);

const simplePhoneMasking = (message) => {
  // Mask any sequence of 5+ digits (phone numbers)
  const simplePhoneRegex = /\b\d{5,}\b/g;
  return message.replace(simplePhoneRegex, '***');
};

// Domain names only
const domainNameRegex = /(?:https?:\/\/)?(?:www\.)?(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}/g;

// IPv4 addresses
const ipv4Regex = /(?:\b(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\b|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\])/g;

// IPv6 addresses
// const ipv6Regex = /(?:[a-fA-F0-9]{1,4}:){7}[a-fA-F0-9]{1,4}|(?:[a-fA-F0-9]{1,4}:){1,7}:|(?:[a-fA-F0-9]{1,4}:){1,6}:[a-fA-F0-9]{1,4}|(?:[a-fA-F0-9]{1,4}:){1,5}(?::[a-fA-F0-9]{1,4}){1,2}|(?:[a-fA-F0-9]{1,4}:){1,4}(?::[a-fA-F0-9]{1,4}){1,3}|(?:[a-fA-F0-9]{1,4}:){1,3}(?::[a-fA-F0-9]{1,4}){1,4}|(?:[a-fA-F0-9]{1,4}:){1,2}(?::[a-fA-F0-9]{1,4}){1,5}|[a-fA-F0-9]{1,4}:(?:(?::[a-fA-F0-9]{1,4}){1,6})|:(?:(?::[a-fA-F0-9]{1,4}){1,7})|::|fe80:(?::[a-fA-F0-9]{0,4}){0,4}%[a-zA-Z0-9]{1,}|::(?:ffff(?::0{1,4}){0,1}:){0,1}(?:(?:25[0-5]|(?:2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(?:25[0-5]|(?:2[0-4]|1{0,1}[0-9]){0,1}[0-9])|(?:[a-fA-F0-9]{1,4}:){1,4}:(?:(?:25[0-5]|(?:2[0-4]|1{0-9]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(?:25[0-5]|(?:2[0-4]|1{0,1}[0-9]){0,1}[0-9]))/g;

// const combinedDomainRegex = new RegExp(
//   `(?:${domainNameRegex.source}|${ipv4Regex.source}|${ipv6Regex.source})`,
//   'g'
// );
const combinedDomainRegex = new RegExp(
  `(?:${domainNameRegex.source}|${ipv4Regex.source})`,
  'g'
);

// Escape keywords for regex safety
const escapeRegex = (str) => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
};

// Build regex dynamically: global + case-insensitive
const keywordRegex = (keywords) => new RegExp(
  keywords.map(k => `\\b${escapeRegex(k)}\\b`).join("|"),
  "gi"
);

// Pre-filter function to identify legitimate content before regex matching
const preFilterLegitimateContent = (message) => {
  // Patterns that indicate legitimate business content (should not be masked)
  const legitimatePatterns = [
    // Time-related patterns
    /\bfrom\s+\d{1,2}:\d{2}(?:am|pm)\s+to\s+\d{1,2}:\d{2}(?:am|pm)\b/i,
    /\b\d{1,2}:\d{2}(?:am|pm)\s+to\s+\d{1,2}:\d{2}(?:am|pm)\b/i,
    /\b\d{1,2}:\d{2}(?:am|pm)\s+tomorrow\b/i,
    /\b\d{1,2}:\d{2}\s+tomorrow\b/i,
    
    // Date patterns
    /\bMeeting\s+scheduled\s+for\s+\d{1,2}\/\d{1,2}\/\d{4}\b/i,
    /\buntil\s+December\s+\d{1,2}st\b/i,
    /\bfor\s+\d{1,2}\/\d{1,2}\/\d{4}\b/i,
    
    // Business context patterns
    /\bHi\s+[^,]+,\s+is\s+your\s+car\s+available\b/i,
    /\bFlight\s+departs\s+at\s+\d{1,2}:\d{2}\b/i,
    /\bDiscount\s+is\s+\d+%\s+off\b/i,
    /\bThe\s+year\s+\d{4}\s+has\s+been\b/i,
    /\bPrice\s+is\s+\$\d+\.\d+\b/i,
    /\bTemperature\s+is\s+\d+\.\d+\s+degrees\b/i,
    /\bAvailable\s+from\s+\d{1,2}:\d{2}(?:am|pm)\s+to\s+\d{1,2}:\d{2}(?:am|pm)\b/i,
    
    // Standalone legitimate patterns
    /^\d{1,4}$/,           // 1234, 2025
    /^\d+%$/,              // 50%
    /^\d+\.\d+$/,          // 3.14
    /^\d{1,2}:\d{2}$/,    // 14:30
    /^\d{1,2}\/\d{1,2}$/, // 12/25
    /^\d{1,2}:\d{2}(?:am|pm)$/i  // 9:30am
  ];
  
  // Check if the message contains any legitimate patterns
  return legitimatePatterns.some(pattern => pattern.test(message));
};

export const doesMessageContainPhoneNumberOrEmail = message => {
  // Pre-filter: If message contains legitimate business content, don't mask
  if (preFilterLegitimateContent(message)) {
    return false;
  }
  
  // Check for emails
  if (emailRegex.test(message)) {
    return true;
  }
  
  // Check for phone numbers
  if (combinedPhoneRegex.test(message)) {
    return true;
  }
  
  // Check for domains
  if (combinedDomainRegex.test(message)) {
    return true;
  }
  
  // Check for banned keywords
  const allBannedKeywords = [
    ...host_to_guest_enquiry_banned_keywords,
    ...guest_to_host_enquiry_banned_keywords,
    ...host_to_guest_booking_banned_keywords,
    ...guest_to_host_booking_banned_keywords
  ];
  
  if (keywordRegex(allBannedKeywords).test(message)) {
    return true;
  }
  
  return false;
};

const guest_to_host_enquiry_banned_keywords = [
  "bank account verify",
  "verify bank",
  "verify bank account",
  "bank account",
  "bank details",
  "bank account details",
  "regulatory update",
  "regulatory",
  "secure personalized link",
  "link",
  "url",
  "uninterrupted payouts",
  "payout",
  "verify your information",
  "trusturl.shop",
  "drivelah team",
  "drive lah team",
  "drivelah support",
  "drive lah support",
  "verify your account",
  "account verification",
  "security update",
  "compliance required",
  "urgent action required",
  "action required",
  "iDrive",
  "Vehicle Rentals in Singapore – Fast, Easy & Affordable with iDrive.SG",
  "idrive",
  "Vehicle Rentals in Singapore – Fast, Easy & Affordable with iDrive.SG",
  "wa.me",
  "Telegram",
  "Whatsapp",
  "Email",
  "bit.ly",
  "earn passively",
  "replacement drivers",
  "list your car on idrive",
  "drive mate team",
  "drive mate support",
  "Drivemate support",
  "Drivemate team",
  "book offline",
  "book direct",
  "direct book",
  "directly book",
  "whatsapp",
  "telegram",
  "DM",
  "signal",
  "email",
  "mail",
  "mail me at",
  "gmail",
  "yahoo",
  "hotmail",
  "outlook",
  "watsapp",
  "wtsp",
  "wtsap"
];

const host_to_guest_enquiry_banned_keywords = [
  "book offline",
  "book direct",
  "directly book",
  "direct book",
  "whatsapp",
  "telegram",
  "DM",
  "signal",
  "email",
  "mail",
  "mail me at",
  "gmail",
  "yahoo",
  "hotmail",
  "outlook",
  "watsapp",
  "whatsapp",
  "whatsap",
  "wtsp",
  "wtsap"
];

const guest_to_host_booking_banned_keywords = [
  "bank account verify",
  "verify bank",
  "verify bank account",
  "bank account",
  "bank details",
  "bank account details",
  "regulatory update",
  "regulatory",
  "secure personalized link",
  "uninterrupted payouts",
  "verify your information",
  "trusturl.shop",
  "verify your account",
  "account verification",
  "security update",
  "compliance required",
  "urgent action required",
  "action required",
  "iDrive",
  "Vehicle Rentals in Singapore – Fast, Easy & Affordable with iDrive.SG",
  "idrive",
  "Vehicle Rentals in Singapore – Fast, Easy & Affordable with iDrive.SG",
  "wa.me",
  "bit.ly",
  "earn passively",
  "replacement drivers",
  "list your car on idrive"
];

const host_to_guest_booking_banned_keywords = [];

// Enhanced validation function to prevent false positives
const validatePhoneMatch = (text, context) => {
  // Special case: If the entire message is just a number/pattern, it's likely legitimate
  if (context.trim() === text.trim()) {
    // Check if it's a legitimate standalone pattern
    if (/^\d{1,4}$/.test(text) || // 1234, 2025
        /^\d+%$/.test(text) || // 50%
        /^\d+\.\d+$/.test(text) || // 3.14
        /^\d{1,2}:\d{2}$/.test(text) || // 14:30
        /^\d{1,2}\/\d{1,2}$/.test(text)) { // 12/25
      return false;
    }
  }
  
  // Create a context window around the matched text to check for legitimate patterns
  const textIndex = context.indexOf(text);
  const contextWindow = context.substring(Math.max(0, textIndex - 50), textIndex + text.length + 50);
  
  // Enhanced whitelist patterns that check the context window
  const contextWhitelistPatterns = [
    // Time patterns
    /\b\d{1,2}:\d{2}(?:am|pm)\b/i,        // 9:30am, 3:30pm
    /\b\d{1,2}:\d{2}\b/,                  // 14:30
    /\bfrom\s+\d{1,2}:\d{2}(?:am|pm)\s+to\s+\d{1,2}:\d{2}(?:am|pm)\b/i, // from 9:30am to 3:30pm
    /\b\d{1,2}:\d{2}(?:am|pm)\s+to\s+\d{1,2}:\d{2}(?:am|pm)\b/i,        // 9:30am to 3:30pm
    /\b\d{1,2}:\d{2}(?:am|pm)\s+tomorrow\b/i,                            // 2:30pm tomorrow
    /\b\d{1,2}:\d{2}\s+tomorrow\b/i,                                     // 14:30 tomorrow
    /\b\d{1,2}:\d{2}(?:am|pm)\s+until\b/i,                               // 9:30am until
    /\b\d{1,2}:\d{2}\s+until\b/i,                                        // 14:30 until
    
    // Date patterns
    /\b\d{1,2}\/\d{1,2}\/\d{4}\b/,        // 12/25/2024
    /\b\d{1,2}\/\d{1,2}\b/,               // 12/25
    /\bfor\s+\d{1,2}\/\d{1,2}\/\d{4}\b/i,                               // for 12/25/2024
    /\buntil\s+December\s+\d{1,2}st\b/i,                                 // until December 31st
    /\bDecember\s+\d{1,2}st\b/i,                                          // December 31st
    
    // Number patterns
    /\b\d+%\b/,                            // 50%
    /\b(19|20)\d{2}\b/,                   // Years: 2024, 1999
    /\b\d+\.\d+\b/,                       // Decimals: 3.14
    /\b\d{1,2}\.\d{1,2}\.\d{1,2}\b/,     // Version 2.1.0
    /\b\d{1,2}\.\d{1,2}\b/,               // Version 2.1
    
    // Context-specific patterns
    /\b(?:Room|Chapter|Page|Version)\s+\d+/i, // Room 101, Chapter 1
    /\b\d{1,2}\s+degrees?\s+Celsius\b/i,                                 // 25.5 degrees Celsius
    /\b\d{1,2}\s+floor\b/i,                                               // first floor
    /\b\d{1,2}\s+of\s+the\b/i,                                           // Page 42 of the
    /\b\d{1,2}\s+per\s+item\b/i,                                         // 3.14 per item
    /\b\d{1,2}\s+off\b/i,                                                 // 50% off
    /\b\d{1,2}\s+has\s+been\b/i,                                         // 2024 has been
    /\b\d{1,2}\s+is\s+now\b/i,                                           // Version 2.1.0 is now
    /\b\d{1,2}\s+is\s+on\b/i,                                             // Room 101 is on
    /\b\d{1,2}\s+is\s+at\b/i,                                             // Flight departs at 14:30
    
    // Business context patterns
    /\bPrice\s+is\s+\$\d+\.\d+\b/i,                                      // Price is $3.14
    /\bTemperature\s+is\s+\d+\.\d+\s+degrees\b/i,                        // Temperature is 25.5 degrees
    /\bAvailable\s+from\s+\d{1,2}:\d{2}(?:am|pm)\s+to\s+\d{1,2}:\d{2}(?:am|pm)\b/i, // Available from 9:00am to 5:00pm
    /\bMeeting\s+scheduled\s+for\s+\d{1,2}\/\d{1,2}\/\d{4}\b/i,          // Meeting scheduled for 12/25/2024
    /\bFlight\s+departs\s+at\s+\d{1,2}:\d{2}\b/i,                        // Flight departs at 14:30
    /\bDiscount\s+is\s+\d+%\s+off\b/i,                                    // Discount is 50% off
    /\bThe\s+year\s+\d{4}\s+has\s+been\b/i,                              // The year 2024 has been
    /\bHi\s+[^,]+,\s+is\s+your\s+car\s+available\b/i,                    // Hi Nasry, is your car available
    /\bfrom\s+\d{1,2}:\d{2}(?:am|pm)\s+to\s+\d{1,2}:\d{2}(?:am|pm)\b/i,  // from 9:30am to 3:30pm
    /\b\d{1,2}:\d{2}(?:am|pm)\s+to\s+\d{1,2}:\d{2}(?:am|pm)\b/i,         // 9:30am to 3:30pm
    /\b\d{1,2}:\d{2}(?:am|pm)\s+tomorrow\b/i,                             // 9:30am tomorrow
    /\b\d{1,2}:\d{2}\s+tomorrow\b/i,                                      // 14:30 tomorrow
    /\buntil\s+December\s+\d{1,2}st\b/i,                                  // until December 31st
    /\bfor\s+\d{1,2}\/\d{1,2}\/\d{4}\b/i,                                // for 12/25/2024
    /\bfrom\s+\d{1,2}:\d{2}(?:am|pm)\b/i,                                // from 9:00am
    /\bto\s+\d{1,2}:\d{2}(?:am|pm)\b/i,                                  // to 5:00pm
    /\btext\s+\d{5,}\b/i,                                                  // text 9123 4567
    /\bcall\s+\(\d{2}\)\s+\d{4}\s+\d{4}\b/i,                              // call (02) 9876 5432
    /\bcontact\s+\+\d{2}\s+\d\s+\d{4}\s+\d{4}\b/i,                        // contact +61 4 1234 5678
    /\bemail\s+[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}\b/i,        // email john@example.com
    /\bor\s+email\s+[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}\b/i,   // or email john@example.com
    /\bbut\s+contact\s+\+\d{2}\s+\d\s+\d{4}\s+\d{4}\b/i,                  // but contact +61 4 1234 5678
    /\bHi,\s+call\s+me\s+at\b/i,                                          // Hi, call me at
    /\bMeeting\s+at\s+\d{1,2}:\d{2}(?:am|pm)\b/i,                         // Meeting at 2:30pm
    /\bDiscount\s+is\s+\d+%\b/i,                                           // Discount is 50%
    /\byear\s+\d{4}\b/i,                                                   // year 2024
    /\bPrice\s+is\s+\$\d+\.\d+\b/i,                                        // Price is $25.50
    /\bTemperature\s+is\s+\d+\.\d+\b/i,                                    // Temperature is 25.5
    /\bRoom\s+\d{3}\b/i,                                                   // Room 101
    /\bChapter\s+\d+\b/i,                                                   // Chapter 1
    /\bVersion\s+\d+\.\d+\.\d+\b/i,                                        // Version 2.1.0
    /\bAvailable\s+from\s+\d{1,2}:\d{2}(?:am|pm)\b/i,                     // Available from 9:00am
    /\bContact\s+me\s+on\b/i,                                              // Contact me on
    /\bLet's\s+book\b/i,                                                    // Let's book
    /\bSend\s+me\s+an\b/i,                                                 // Send me an
    /\bUse\s+telegram\b/i,                                                  // Use telegram
    /\bDM\s+me\s+for\b/i                                                   // DM me for
  ];
  
  // Check context whitelist patterns
  if (contextWhitelistPatterns.some(pattern => pattern.test(contextWindow))) {
    return false;
  }
  
  // Don't mask if it's part of a larger word
  if (/[a-zA-Z]\d+[a-zA-Z]/.test(context)) {
    return false;
  }
  
  // Don't mask if it's clearly part of legitimate text context
  const legitimateContextKeywords = [
    'Meeting', 'Discount', 'year', 'Price', 'Flight', 'Temperature', 
    'Room', 'Chapter', 'Version', 'Available', 'from', 'to', 'tomorrow', 
    'until', 'December', 
    'but', 'departs', 'scheduled', 
    'off', 'has been', 'is now', 'is on', 'is at', 'per item', 'degrees', 
    'floor', 'of the', 'car available', 'scheduled for', 'departs at', 
    'until December', 'for business', 'from', 'to'
  ];
  
  if (legitimateContextKeywords.some(keyword => 
    contextWindow.toLowerCase().includes(keyword.toLowerCase())
  )) {
    return false;
  }
  
  // Must have sufficient digits to be a phone number
  const digitCount = (text.match(/\d/g) || []).length;
  if (digitCount < 5) {
    return false;
  }
  
  return true;
};

// Additional context validation function to catch specific problematic patterns
const validateContextBeforeMasking = (text, context) => {
  // Check for specific problematic patterns that should never be masked
  const problematicPatterns = [
    // Time ranges
    /\bfrom\s+\d{1,2}:\d{2}(?:am|pm)\s+to\s+\d{1,2}:\d{2}(?:am|pm)\b/i,
    /\b\d{1,2}:\d{2}(?:am|pm)\s+to\s+\d{1,2}:\d{2}(?:am|pm)\b/i,
    
    // Business context
    /\bHi\s+[^,]+,\s+is\s+your\s+car\s+available\b/i,
    /\bMeeting\s+scheduled\s+for\s+\d{1,2}\/\d{1,2}\/\d{4}\b/i,
    /\bFlight\s+departs\s+at\s+\d{1,2}:\d{2}\b/i,
    /\bDiscount\s+is\s+\d+%\s+off\b/i,
    /\bThe\s+year\s+\d{4}\s+has\s+been\b/i,
    /\bPrice\s+is\s+\$\d+\.\d+\b/i,
    /\bTemperature\s+is\s+\d+\.\d+\s+degrees\b/i,
    /\bAvailable\s+from\s+\d{1,2}:\d{2}(?:am|pm)\s+to\s+\d{1,2}:\d{2}(?:am|pm)\b/i,
    
    // Standalone legitimate patterns
    /^\d{1,4}$/,           // 1234, 2025
    /^\d+%$/,              // 50%
    /^\d+\.\d+$/,          // 3.14
    /^\d{1,2}:\d{2}$/,    // 14:30
    /^\d{1,2}\/\d{1,2}$/, // 12/25
    /^\d{1,2}:\d{2}(?:am|pm)$/i  // 9:30am
  ];
  
  // Check if the text matches any problematic pattern
  if (problematicPatterns.some(pattern => pattern.test(text))) {
    return false;
  }
  
  // Check if the context contains legitimate business keywords
  const businessKeywords = [
    'car available', 'scheduled for', 'departs at', 'discount', 'year', 
    'price', 'temperature', 'degrees', 'available from', 'to', 'tomorrow',
    'until', 'December', 'business', 'meeting', 'flight'
  ];
  
  if (businessKeywords.some(keyword => context.toLowerCase().includes(keyword.toLowerCase()))) {
    return false;
  }
  
  return true;
};

const maskingForEnquiryHost = message => {
  let maskedText = message;

  // Simple 6+ digits masking
  maskedText = simplePhoneMasking(maskedText);
  
  // Mask emails
  maskedText = maskedText.replace(emailRegex, '***');
  
  // Mask phone numbers with validation and consistent masking
  maskedText = maskedText.replace(combinedPhoneRegex, (match) => {
    // First check context validation
    if (!validateContextBeforeMasking(match, message)) {
      return match; // Keep original if context validation fails
    }
    
    // Then check phone validation
    if (validatePhoneMatch(match, message)) {
      // Ensure consistent masking - replace entire phone number
      return '***';
    }
    return match; // Keep original if validation fails
  });
  
  // Mask sequential number words (5+ in a row representing phone numbers)
  maskedText = maskedText.replace(sequentialNumberWordsRegex, '***');
  
  // Mask banned keywords
  return maskedText.replace(keywordRegex(host_to_guest_enquiry_banned_keywords), "***");
};

const maskingForEnquiryGuest = message => {
  let maskedText = message;

  // Simple 6+ digits masking
  maskedText = simplePhoneMasking(maskedText);
  
  // Mask emails
  maskedText = maskedText.replace(emailRegex, '***');
  
  // Mask phone numbers with validation and consistent masking
  maskedText = maskedText.replace(combinedPhoneRegex, (match) => {
    // First check context validation
    if (!validateContextBeforeMasking(match, message)) {
      return match; // Keep original if context validation fails
    }
    
    // Then check phone validation
    if (validatePhoneMatch(match, message)) {
      // Ensure consistent masking - replace entire phone number
      return '***';
    }
    return match; // Keep original if validation fails
  });
  
  // Mask domains
  maskedText = maskedText.replace(combinedDomainRegex, '***');
  
  // Mask sequential number words (5+ in a row representing phone numbers)
  maskedText = maskedText.replace(sequentialNumberWordsRegex, '***');
  
  // Mask banned keywords
  return maskedText.replace(keywordRegex(guest_to_host_enquiry_banned_keywords), "***");
};

const maskingForBookingHost = message => {
  return message;
  // const maskedText = message
  // .replace(emailRegex, '***')
  // .replace(combinedPhoneRegex, '***')
  // .replace(combinedDomainRegex, '***');
  // 
  // return maskedText.replace(keywordRegex(host_to_guest_booking_banned_keywords), "***");
};

const maskingForBookingGuest = message => {
  let maskedText = message;
  
  // Mask domains with validation (if needed)
  // .replace(combinedDomainRegex, '***');
  
  // Mask banned keywords
  return maskedText.replace(keywordRegex(guest_to_host_booking_banned_keywords), "***");
};

export const encodeMsgContainingPhoneNoOrEmailMaybe = ({message = '', isEnquiry = false, isHost = false}) => {

  let maskedMessage = message;
  if(isEnquiry && isHost) {
    // Host is sending message to Guest in Enquiry
    maskedMessage = maskingForEnquiryHost(message);

  } else if(isEnquiry && !isHost) {
    // Guest is sending message to Host in Enquiry
    maskedMessage = maskingForEnquiryGuest(message);

  } else if(!isEnquiry && isHost) {
    // Host is sending message to Guest in Booking
    maskedMessage = maskingForBookingHost(message);
    return maskedMessage;

  } else if(!isEnquiry && !isHost) {
    // Guest is sending message to Host in Booking
    maskedMessage = maskingForBookingGuest(message);
  }

  const isMessageMasked = maskedMessage !== message;

  return `${maskedMessage} ${isMessageMasked ? '(some parts of this message are masked to comply with the platform guidelines)' : ''}`;
};

export const validBusinessURL = message => value => {
  if (typeof value === 'undefined' || value === null) {
    return message;
  }

  const disallowedChars = /[^-A-Za-z0-9+&@#/%?=~_|!:,.;()]/;
  const protocolTokens = value.split(':');
  const includesProtocol = protocolTokens.length > 1;
  const usesHttpProtocol = includesProtocol && !!protocolTokens[0].match(/^(https?)/);

  const invalidCharacters = !!value.match(disallowedChars);
  const invalidProtocol = !(usesHttpProtocol || !includesProtocol);
  // Stripe checks against example.com
  const isExampleDotCom = !!value.match(/^(https?:\/\/example\.com|example\.com)/);
  const isLocalhost = !!value.match(/^(https?:\/\/localhost($|:|\/)|localhost($|:|\/))/);
  return invalidCharacters || invalidProtocol || isExampleDotCom || isLocalhost ? message : VALID;
};

export const validSsnLast4 = message => value => {
  return value.length === 4 ? VALID : message;
};

export const validHKID = message => value => {
  // Accept value 000000000 for testing Stripe
  if (value.length === 9 && value.match(/([0]{9})/)) {
    return VALID;
  }

  // HKID format example: AB364912(5)
  // ID can start with one or two letters and the check digit in the end can be in brackets or not
  if (value.length < 8) {
    return message;
  }

  // Handle possible brackets in value
  if (value.charAt(value.length - 3) === '(' && value.charAt(value.length - 1) === ')') {
    value = value.substring(0, value.length - 3) + value.charAt(value.length - 2);
  }
  value = value.toUpperCase();

  // Check that pattern is correct and split value to array
  const hkidPattern = /^([A-Z]{1,2})([0-9]{6})([A0-9])$/;
  const matchArray = value.match(hkidPattern);

  if (!matchArray) {
    return message;
  }

  const charPart = matchArray[1];
  const numPart = matchArray[2];
  const checkDigit = matchArray[3];

  // Calculate the checksum for character part.
  // Transfer letters to numbers so that A=10, B=11, C=12 etc.
  // If there is only one letter in the ID use 36 as the first value
  // Total calculation is weighted so that 1st digit is x9, 2nd digit x8, 3rd digit x7 etc.

  const strValidChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  let checkSum = 0;

  if (charPart.length === 2) {
    checkSum += 9 * (10 + strValidChars.indexOf(charPart.charAt(0)));
    checkSum += 8 * (10 + strValidChars.indexOf(charPart.charAt(1)));
  } else {
    checkSum += 9 * 36;
    checkSum += 8 * (10 + strValidChars.indexOf(charPart));
  }

  // Calculate the checksum for numeric part

  for (let i = 0, j = 7; i < numPart.length; i++, j--) {
    checkSum += j * numPart.charAt(i);
  }

  // Verify the check digit
  const remaining = checkSum % 11;
  let verify = remaining === 0 ? 0 : 11 - remaining;
  verify = verify.toString();
  const isValid = verify === checkDigit || (verify === 10 && checkDigit === 'A');

  return isValid ? VALID : message;
};

export const isValidReferralCode = message => value => {
  if (typeof value === 'undefined') return VALID;
  if (value.trim().length === 0 || checkIfReferralCode(value.trim())) return VALID;
  return message;
};

export const validSGID = message => value => {
  // Accept value 000000000 for testing Stripe
  if (value.length === 9 && value.match(/([0]{9})/)) {
    return VALID;
  }

  if (value.length !== 9) return message;

  value = value.toUpperCase();

  let i,
    icArray = [];
  for (i = 0; i < 9; i++) {
    icArray[i] = value.charAt(i);
  }

  icArray[1] = parseInt(icArray[1], 10) * 2;
  icArray[2] = parseInt(icArray[2], 10) * 7;
  icArray[3] = parseInt(icArray[3], 10) * 6;
  icArray[4] = parseInt(icArray[4], 10) * 5;
  icArray[5] = parseInt(icArray[5], 10) * 4;
  icArray[6] = parseInt(icArray[6], 10) * 3;
  icArray[7] = parseInt(icArray[7], 10) * 2;

  let weight = 0;
  for (i = 1; i < 8; i++) {
    weight += icArray[i];
  }

  let offset = icArray[0] === 'T' || icArray[0] === 'G' ? 4 : 0;
  let temp = (offset + weight) % 11;

  let st = ['J', 'Z', 'I', 'H', 'G', 'F', 'E', 'D', 'C', 'B', 'A'];
  let fg = ['X', 'W', 'U', 'T', 'R', 'Q', 'P', 'N', 'M', 'L', 'K'];

  let theAlpha;
  if (icArray[0] === 'S' || icArray[0] === 'T') {
    theAlpha = st[temp];
  } else if (icArray[0] === 'F' || icArray[0] === 'G') {
    theAlpha = fg[temp];
  }

  return icArray[8] === theAlpha ? VALID : message;
};

export const composeValidators = (...validators) => value =>
  validators.reduce((error, validator) => error || validator(value), VALID);

export const minValue = (message, min) => value => {
  return parseInt(value) < min ? message : VALID;
};

export const maxValue = (message, max) => value => {
  return parseInt(value) > max ? message : VALID;
};

export const notFoundValue = (message, values = []) => value => {
  if (values.find(item => item.label === value)) return VALID;
  return message;
};
